'use strict'

import React, { useState } from 'react'
import { Formik } from 'formik'
import Icon from '@mdi/react'
import { mdiCurrencyUsd } from '@mdi/js'

import Box from '@components/kit/box'
import Card from '@components/kit/card'
import Text from '@components/kit/text'
import Button from '@components/kit/button'
import Pressable from '@components/kit/pressable'
import { ChevronDownIcon } from '@components/kit/icons'
import { colors } from '@theme'

import { TextFieldFormated } from '@components/formFields'
import { Markdown } from '@components/markdown'
import { useMemberSavingsStore } from '@store/MemberSavings'
import {
  IMemberSaving,
  MemberSavingsCategory
} from '@store/MemberSavings/types'

const SAVINGS_INPUT_WIDTH = '140px'

export type MemberSavingsItemProps = {
  index: number
  item: IMemberSaving
  userId: number
  category: MemberSavingsCategory
  onUpdated: (item: IMemberSaving) => void
  onRejected: (savingId: number) => void
}

function formatSavingsValue(value?: number | null): string {
  if (value === null || value === undefined) {
    return ''
  }
  return String(value)
}

function parseSavingsValue(text: string): number | null {
  const trimmed = text.trim()
  if (!trimmed) {
    return null
  }
  const parsed = Number(trimmed)
  return Number.isFinite(parsed) ? parsed : null
}

function MemberSavingsItem({
  index,
  item,
  userId,
  category,
  onUpdated,
  onRejected
}: MemberSavingsItemProps) {
  const [expanded, setExpanded] = useState(false)
  const [clientCostSavings, setClientCostSavings] = useState(
    formatSavingsValue(item.client_cost_savings)
  )
  const [memberCostSavings, setMemberCostSavings] = useState(
    formatSavingsValue(item.member_cost_savings)
  )
  const [isSaving, setIsSaving] = useState(false)
  const [isRejecting, setIsRejecting] = useState(false)

  const { updateMemberSavingCosts, rejectMemberSaving } =
    useMemberSavingsStore.getState()

  const handleSaveCosts = async () => {
    setIsSaving(true)
    try {
      const payload = {
        client_cost_savings: parseSavingsValue(clientCostSavings),
        member_cost_savings: parseSavingsValue(memberCostSavings),
        status: item.status
      }
      const result = await updateMemberSavingCosts(userId, item.id, payload)
      if (result.success) {
        onUpdated({ ...item, ...payload })
      }
    } finally {
      setIsSaving(false)
    }
  }

  const handleReject = async () => {
    setIsRejecting(true)
    try {
      const result = await rejectMemberSaving(userId, item.id, {
        client_cost_savings: item.client_cost_savings,
        member_cost_savings: item.member_cost_savings
      })
      if (result.success) {
        onRejected(item.id)
      }
    } finally {
      setIsRejecting(false)
    }
  }

  const isCostSavings = category === 'cost_savings'
  const isBusy = isSaving || isRejecting

  return (
    <Card paddingY='16' paddingLeft='16' paddingRight='24' marginX='12'>
      <Pressable onPress={() => setExpanded(prev => !prev)}>
        <Box
          flexDirection='row'
          alignItems='center'
          justifyContent='space-between'
        >
          <Text
            font='display.16.bold'
            textColor='walrusBlue200'
            flex='1 1 auto'
            pr='08'
          >
            {`${index}. ${item.title}`}
          </Text>
          <Box
            style={{
              transform: [{ rotate: expanded ? '180deg' : '0deg' }]
            }}
          >
            <ChevronDownIcon fill='walrusBlue500' width={20} height={20} />
          </Box>
        </Box>
      </Pressable>

      {expanded && (
        <Box mt='12'>
          <Box>
            <Markdown>{item.description}</Markdown>
          </Box>

          {isCostSavings && (
            <Formik
              initialValues={{
                client_cost_savings: formatSavingsValue(
                  item.client_cost_savings
                ),
                member_cost_savings: formatSavingsValue(
                  item.member_cost_savings
                )
              }}
              enableReinitialize
              onSubmit={() => {}}
            >
              <Box mt='12' gap='08'>
                <Box flexDirection='row' alignItems='center' gap='12'>
                  <Box flex='1 1 auto' minWidth={0} pr='08'>
                    <Text font='display.14.bold' textColor='walrusBlue200'>
                      • Estimated Annual Member Savings:
                    </Text>
                  </Box>
                  <Box width={SAVINGS_INPUT_WIDTH}>
                    <TextFieldFormated
                      name='member_cost_savings'
                      label=''
                      value={memberCostSavings}
                      valuePlaceholder='0'
                      onChangeText={setMemberCostSavings}
                      keyboardType='numeric'
                      compact
                      fieldPrefix={
                        <Icon
                          path={mdiCurrencyUsd}
                          size='14px'
                          color={colors.walrusBlue200}
                        />
                      }
                      mb='00'
                    />
                  </Box>
                </Box>

                <Box flexDirection='row' alignItems='center' gap='12'>
                  <Box flex='1 1 auto' minWidth={0} pr='08'>
                    <Text font='display.14.bold' textColor='walrusBlue200'>
                      • Estimated Annual Plan Savings:
                    </Text>
                  </Box>
                  <Box width={SAVINGS_INPUT_WIDTH}>
                    <TextFieldFormated
                      name='client_cost_savings'
                      label=''
                      value={clientCostSavings}
                      valuePlaceholder='0'
                      onChangeText={setClientCostSavings}
                      keyboardType='numeric'
                      compact
                      fieldPrefix={
                        <Icon
                          path={mdiCurrencyUsd}
                          size='14px'
                          color={colors.walrusBlue200}
                        />
                      }
                      mb='00'
                    />
                  </Box>
                </Box>
              </Box>
            </Formik>
          )}

          <Box mt='12' flexDirection='row' justifyContent='flex-end' gap='08'>
            {isCostSavings && (
              <Button
                variant='secondary'
                size='small'
                onPress={handleSaveCosts}
                disabled={isBusy}
              >
                Save
              </Button>
            )}
            <Button
              variant='paleRed'
              size='small'
              onPress={handleReject}
              disabled={isBusy}
            >
              Reject
            </Button>
          </Box>
        </Box>
      )}
    </Card>
  )
}

export default MemberSavingsItem
